/* Korean Member email preferences use Ghost's session-authenticated account API. */
(function () {
  const mount = document.querySelector('[data-member-email-settings]');
  if (!mount) return;

  const create = (tag, text, className) => {
    const node = document.createElement(tag);
    if (text) node.textContent = text;
    if (className) node.className = className;
    return node;
  };
  const showSignedOut = () => {
    const message = create('p', '이메일 설정을 보려면 로그인해 주세요.', 'billing-info');
    const link = create('a', '로그인');
    link.href = '/signin/';
    mount.replaceChildren(message, link);
  };
  const showInitialError = () => {
    mount.replaceChildren(create('p', '이메일 설정을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.', 'billing-info billing-error'));
  };
  const rows = () => Array.from(mount.querySelectorAll('[data-member-newsletter]'));
  const commentInput = () => mount.querySelector('[data-member-comment-notifications]');
  const newsletterSection = mount.querySelector('[data-email-settings-newsletters]');
  const commentSection = mount.querySelector('[data-comment-preference]');
  const feedback = create('p', '', 'member-email-settings-feedback billing-info billing-error');
  feedback.setAttribute('role', 'alert');
  feedback.hidden = true;
  const saveButton = create('button', '변경사항 저장', 'billing-primary-action member-email-settings-save');
  saveButton.type = 'button';
  saveButton.disabled = true;
  let baseline = null;
  let saving = false;
  let locked = false;

  const readState = async () => {
    const response = await fetch('/members/api/member/', {
      credentials: 'same-origin',
      headers: {Accept: 'application/json'}
    });
    if (response.status === 204 || response.status === 401) return {anonymous: true};
    if (!response.ok) throw new Error('member_read_failed');
    return response.json();
  };

  const activeIds = () => rows().map((input) => input.dataset.newsletterId);
  const normalizedIds = (ids) => [...new Set(ids.map(String))].sort();
  const currentDraft = () => ({
    newsletters: normalizedIds(rows().filter((input) => input.checked).map((input) => input.dataset.newsletterId)),
    enable_comment_notifications: commentInput()?.checked === true
  });
  const applyState = (member) => {
    const active = new Set(activeIds());
    const subscribed = new Set((Array.isArray(member.newsletters) ? member.newsletters : []).map((newsletter) => String(newsletter.id)));
    rows().forEach((input) => {
      input.checked = subscribed.has(input.dataset.newsletterId);
    });
    if (commentInput()) commentInput().checked = member.enable_comment_notifications === true;
    baseline = {
      newsletters: normalizedIds([...subscribed].filter((id) => active.has(id))),
      enable_comment_notifications: member.enable_comment_notifications === true
    };
  };
  const updateDirty = () => {
    if (!baseline || saving || locked) {
      saveButton.disabled = true;
      return;
    }
    const draft = currentDraft();
    saveButton.disabled = draft.enable_comment_notifications === baseline.enable_comment_notifications
      && draft.newsletters.length === baseline.newsletters.length
      && draft.newsletters.every((id, index) => id === baseline.newsletters[index]);
  };
  const setControlsDisabled = (disabled) => {
    [...rows(), commentInput()].filter(Boolean).forEach((input) => {
      input.disabled = disabled;
    });
    saveButton.disabled = disabled || !baseline || !isDirty();
  };
  const isDirty = () => {
    if (!baseline) return false;
    const draft = currentDraft();
    return draft.enable_comment_notifications !== baseline.enable_comment_notifications
      || draft.newsletters.length !== baseline.newsletters.length
      || draft.newsletters.some((id, index) => id !== baseline.newsletters[index]);
  };
  const setFeedback = (message) => {
    feedback.textContent = message;
    feedback.hidden = !message;
  };
  const lockForReload = () => {
    locked = true;
    setControlsDisabled(true);
    setFeedback('저장 결과를 확인할 수 없습니다. 페이지를 새로고침해 현재 설정을 확인해 주세요.');
  };

  const save = async () => {
    if (saving || locked || !isDirty()) return;
    saving = true;
    const draft = currentDraft();
    const payload = {
      newsletters: draft.newsletters.map((id) => ({id})),
      enable_comment_notifications: draft.enable_comment_notifications
    };
    saveButton.textContent = '저장 중...';
    setControlsDisabled(true);
    setFeedback('');

    try {
      const response = await fetch('/members/api/member/', {
        method: 'PUT',
        credentials: 'same-origin',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('member_update_failed');

      let authoritative;
      try {
        authoritative = await readState();
      } catch (_) {
        lockForReload();
        return;
      }
      if (authoritative.anonymous) {
        showSignedOut();
        return;
      }

      applyState(authoritative);
      updateDirty();
      const observed = currentDraft();
      const matches = observed.enable_comment_notifications === draft.enable_comment_notifications
        && observed.newsletters.length === draft.newsletters.length
        && observed.newsletters.every((id, index) => id === draft.newsletters[index]);
      if (!matches) setFeedback('저장 결과를 확인하지 못했습니다. 현재 설정을 다시 확인해 주세요.');
    } catch (_) {
      let authoritative;
      try {
        authoritative = await readState();
      } catch (_) {
        lockForReload();
        return;
      }
      if (authoritative.anonymous) {
        showSignedOut();
        return;
      }
      applyState(authoritative);
      updateDirty();
      setFeedback('변경 사항을 저장하지 못했습니다. 현재 설정을 다시 확인해 주세요.');
    } finally {
      saving = false;
      saveButton.textContent = '변경사항 저장';
      if (!locked) setControlsDisabled(false);
      updateDirty();
    }
  };

  Promise.resolve().then(readState).then((member) => {
    if (member.anonymous) {
      showSignedOut();
      return;
    }
    applyState(member);
    const newsletterHeading = create('h2', '뉴스레터', 'billing-title');
    newsletterHeading.id = 'member-email-newsletter-heading';
    if (newsletterSection) {
      newsletterSection.hidden = false;
      newsletterSection.setAttribute('aria-labelledby', newsletterHeading.id);
    }
    if (commentSection) commentSection.hidden = false;
    const commentHeading = create('h2', '댓글', 'billing-title');
    commentHeading.id = 'member-email-comment-heading';
    if (commentSection) commentSection.setAttribute('aria-labelledby', commentHeading.id);
    mount.replaceChildren(
      newsletterHeading,
      ...(newsletterSection ? [newsletterSection] : [create('p', '현재 받을 수 있는 뉴스레터가 없습니다.', 'billing-info')]),
      commentHeading,
      ...(commentSection ? [commentSection] : []),
      create('p', '이메일 수신 설정을 변경해도 유료 구독은 변경되지 않습니다.', 'billing-info'),
      saveButton,
      feedback
    );
    rows().forEach((input) => input.addEventListener('change', updateDirty));
    if (commentInput()) commentInput().addEventListener('change', updateDirty);
    saveButton.addEventListener('click', save);
    updateDirty();
  }).catch(showInitialError);
})();
